// Series visibility for the Digital Twin "Analyse" comparison dialog
// (PagesContent/DigitalTwinV3/Tabs/BatteryVitals/ComparisonChart).
//
// One shared legend sits above one chart per selected metric. The legend owns a single
// set of hidden series keys; every chart derives its rendered series from that set with
// applyHiddenSeries, and the synced tooltip skips the same keys. Nothing calls
// ApexCharts.hideSeries, so there is no per-chart state that can drift.
//
// Series key
// ----------
// The comparison builders (getDigitalTwinChartDataFor*InComparison in
// DigitalTwinFunctions.js) name a series "<node.label> : <node.displayId>" and, in
// min-avg-max mode, add a rangeArea companion "<same name> mix". That name is NOT unique
// at every level: e.g. in the backend's "pmc" hierarchy with a customer pack ID, cells of
// different modules in one pack share both label ("Cell 1") and displayName
// ("<pack>_C1"). So the builders also stamp every series with `legendKey`
// ("<node position>:<node id>"), shared by a node's line and its rangeArea companion, and
// identical for that node across every metric. The key falls back to the base name for
// series that carry no legendKey.

export const COMPARISON_MIX_SUFFIX = ' mix';

// Shared default for "nothing hidden": one instance, so a prop default does not
// look like a new value on every render.
export const EMPTY_HIDDEN_KEYS = new Set();

export const isRangeAreaSeries = (series) => series?.type === 'rangeArea';

/**
 * Key that ties a series to its legend item. A rangeArea companion resolves to the same
 * key as its line. Only a rangeArea series has " mix" stripped, so a node label that
 * itself ends in " mix" is never mistaken for a companion.
 */
export const getComparisonSeriesKey = (series) => {
    if (!series) return '';
    if (series.legendKey !== undefined && series.legendKey !== null && series.legendKey !== '') {
        return String(series.legendKey);
    }
    const name = series.name == null ? '' : String(series.name);
    if (isRangeAreaSeries(series) && name.endsWith(COMPARISON_MIX_SUFFIX)) {
        return name.slice(0, -COMPARISON_MIX_SUFFIX.length);
    }
    return name;
};

const toKeySet = (hiddenKeys) => {
    if (hiddenKeys instanceof Set) return hiddenKeys;
    if (Array.isArray(hiddenKeys)) return new Set(hiddenKeys);
    return new Set();
};

export const isComparisonSeriesHidden = (series, hiddenKeys) => {
    const keys = toKeySet(hiddenKeys);
    if (keys.size === 0) return false;
    return keys.has(getComparisonSeriesKey(series));
};

/**
 * The series to render: hidden ones keep their slot (same length and order, so the
 * colour / fill / stroke arrays built from the list stay aligned) but get data: [].
 * Returns the input untouched when nothing is hidden.
 */
export const applyHiddenSeries = (series, hiddenKeys) => {
    if (!Array.isArray(series)) return [];
    const keys = toKeySet(hiddenKeys);
    if (keys.size === 0) return series;
    return series.map((s) => (isComparisonSeriesHidden(s, keys) ? { ...s, data: [] } : s));
};

/** New Set with `key` toggled. Never mutates the input. */
export const toggleHiddenSeriesKey = (hiddenKeys, key) => {
    const next = new Set(toKeySet(hiddenKeys));
    if (next.has(key)) {
        next.delete(key);
    } else {
        next.add(key);
    }
    return next;
};

/**
 * Hidden keys to keep after the legend is rebuilt from a new fetch. Keys that are no
 * longer in the legend (node set or level changed) are dropped; a refetch of the same
 * nodes (zoom, date, granularity) keeps them. Returns the same Set when nothing changes
 * so React can skip the re-render.
 */
export const pruneHiddenSeriesKeys = (hiddenKeys, legendSeries) => {
    const keys = toKeySet(hiddenKeys);
    if (keys.size === 0) return keys;
    const available = new Set((legendSeries || []).map(getComparisonSeriesKey));
    const next = new Set([...keys].filter((key) => available.has(key)));
    return next.size === keys.size ? keys : next;
};
